import { useMemo } from 'react'

import {
  AdCampaignStrategyEnum,
  AdVisualDescriptionQuestion,
} from 'src/Redux/ad-campaignApi'

interface QuestionConfig {
  question: string
  placeholder: string
}

export const QUESTIONS_CONFIG: Record<
  AdCampaignStrategyEnum,
  QuestionConfig[]
> = {
  [AdCampaignStrategyEnum.TRACK]: [
    {
      question: '1. What mood does your track create?',
      placeholder: 'e.g., chill and relaxed',
    },
    {
      question: '2. When is it best to listen to your track?',
      placeholder: 'e.g., party with friends',
    },
    {
      question: '3. What feelings do you want listeners to take away?',
      placeholder: 'e.g., energy',
    },
    {
      question: '4. If your track were a scene, what would it look like?',
      placeholder: 'e.g., sunset at the beach',
    },
  ],
  [AdCampaignStrategyEnum.PLAYLIST]: [
    {
      question: '1. What mood does your playlist create?',
      placeholder: 'e.g., chill and relaxed',
    },
    {
      question: '2. When is it best to listen to this playlist?',
      placeholder: 'e.g., gym workout',
    },
    {
      question: '3. What feelings do you want listeners to take away?',
      placeholder: 'e.g., energy',
    },
    {
      question: '4. If your playlist were a scene, what would it look like?',
      placeholder: 'e.g., sunset at the beach',
    },
  ],
}

interface QuestionData {
  question: string
  answer: string
  placeholder?: string
  fieldName: string
}

export const useOptionalQuestions = (
  visualQuestions?: AdVisualDescriptionQuestion[] | null,
  strategyType?: AdCampaignStrategyEnum,
): QuestionData[] | null => {
  const activeConfig = useMemo(
    () => (strategyType ? QUESTIONS_CONFIG[strategyType] : []),
    [strategyType],
  )

  const questionsSet = useMemo(() => {
    if (!visualQuestions || visualQuestions.length === 0) {
      return activeConfig.map(({ question, placeholder }, index) => ({
        question,
        placeholder,
        answer: '',
        fieldName: `question_${index}`,
      }))
    }

    const serverQuestionsMap = new Map(
      visualQuestions.map((q) => [q.question, q.answer]),
    )

    const allQuestions = new Set([
      ...visualQuestions.map((q) => q.question),
      ...activeConfig.map((c) => c.question),
    ])

    return Array.from(allQuestions).map((question, index) => {
      const configItem = activeConfig.find((c) => c.question === question)
      const serverAnswer = serverQuestionsMap.get(question)

      return {
        question,
        answer: serverAnswer || '',
        placeholder: configItem?.placeholder,
        fieldName: `question_${index}`,
      }
    })
  }, [visualQuestions, activeConfig])

  if (!questionsSet || questionsSet.length === 0) {
    return null
  }

  return questionsSet
}
